import React, { useState, useEffect, useCallback } from 'react';
import { Plus, Edit, Trash2, History, CalendarDays, Building2, ShieldCheck, Clock, FileText, Users, TrendingUp } from 'lucide-react';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Switch } from '../ui/switch';
import { toast } from '../../hooks/use-toast';
import api from '../../services/api';
import { MultiDateSettingsCard } from './MultiDateSettingsCard';

const MONTHS = [
  { value: 1, label: 'January' }, { value: 2, label: 'February' },
  { value: 3, label: 'March' }, { value: 4, label: 'April' },
  { value: 5, label: 'May' }, { value: 6, label: 'June' },
  { value: 7, label: 'July' }, { value: 8, label: 'August' },
  { value: 9, label: 'September' }, { value: 10, label: 'October' },
  { value: 11, label: 'November' }, { value: 12, label: 'December' },
];

const DAYS_IN_MONTH = {
  1: 31, 2: 28, 3: 31, 4: 30, 5: 31, 6: 30,
  7: 31, 8: 31, 9: 30, 10: 31, 11: 30, 12: 31
};

const DEFAULT_FORM = {
  rule_name: '',
  department: 'All',
  employee_grade: 'All',
  effective_from: '',
  cycle_start_month: '1',
  cycle_start_day: '1',
  cycle_end_month: '12',
  cycle_end_day: '31',
  annual_allocation: '30',
  max_carry_forward: '15',
  max_total_usable: '',
  accrual_type: 'upfront',
  reason_for_change: '',
  status: 'active'
};

const GRADES = ['All', 'Staff', 'Manager', 'Head of Department', 'Director'];
const ACCRUAL_TYPES = [
  { value: 'upfront', label: 'Upfront (Full allocation at cycle start)' },
  { value: 'monthly', label: 'Monthly Accrual' },
  { value: 'quarterly', label: 'Quarterly Accrual' },
];

const LeaveRulesTab = ({ isViewer }) => {
  const [rules, setRules] = useState([]);
  const [upcomingRules, setUpcomingRules] = useState([]);
  const [auditLog, setAuditLog] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [auditDialogOpen, setAuditDialogOpen] = useState(false);
  const [editingRule, setEditingRule] = useState(null);
  const [form, setForm] = useState({ ...DEFAULT_FORM });
  const [cfSettings, setCfSettings] = useState({ max_carry_forward: 15, cap_start_year: 2023 });
  const [savingCf, setSavingCf] = useState(false);

  const loadData = useCallback(async () => {
    try {
      const [rulesRes, upcomingRes, deptsRes, cfRes] = await Promise.all([
        api.get('/api/leave-rules/'),
        api.get('/api/leave-rules/upcoming'),
        api.get('/api/employees?active_only=true'),
        api.get('/api/leave-rules/carry-forward-settings').catch(() => null),
      ]);
      setRules(rulesRes.data);
      setUpcomingRules(upcomingRes.data);
      const uniqueDepts = [...new Set(deptsRes.data.map(e => e.department).filter(Boolean))].sort();
      setDepartments(uniqueDepts);
      if (cfRes?.data) setCfSettings(cfRes.data);
    } catch {
      toast({ title: 'Error', description: 'Failed to load leave rules', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  const saveCfSettings = async () => {
    setSavingCf(true);
    try {
      const res = await api.put('/api/leave-rules/carry-forward-settings', {
        max_carry_forward: Number(cfSettings.max_carry_forward),
        cap_start_year: Number(cfSettings.cap_start_year),
      });
      setCfSettings(res.data);
      toast({ title: 'Saved', description: 'Carry-forward policy updated. Recalculates on next leave/payroll view.' });
    } catch (e) {
      toast({ title: 'Error', description: e?.response?.data?.detail || 'Failed to save', variant: 'destructive' });
    } finally {
      setSavingCf(false);
    }
  };

  useEffect(() => {
    loadData();
    // Seed default rule
    api.post('/api/leave-rules/seed-default').catch(() => {});
  }, [loadData]);

  const loadAudit = async (ruleId) => {
    try {
      const url = ruleId ? `/api/leave-rules/audit?rule_id=${ruleId}` : '/api/leave-rules/audit';
      const res = await api.get(url);
      setAuditLog(res.data);
      setAuditDialogOpen(true);
    } catch {
      toast({ title: 'Error', description: 'Failed to load audit log', variant: 'destructive' });
    }
  };

  const handleSave = async () => {
    if (!form.rule_name) {
      toast({ title: 'Error', description: 'Rule name is required', variant: 'destructive' });
      return;
    }
    if (!form.effective_from) {
      toast({ title: 'Error', description: 'Effective from date is required', variant: 'destructive' });
      return;
    }

    const payload = {
      rule_name: form.rule_name,
      department: form.department,
      employee_grade: form.employee_grade,
      effective_from: form.effective_from,
      cycle_start_month: parseInt(form.cycle_start_month),
      cycle_start_day: parseInt(form.cycle_start_day),
      cycle_end_month: parseInt(form.cycle_end_month),
      cycle_end_day: parseInt(form.cycle_end_day),
      annual_allocation: parseFloat(form.annual_allocation) || 30,
      max_carry_forward: parseFloat(form.max_carry_forward) || 15,
      max_total_usable: form.max_total_usable ? parseFloat(form.max_total_usable) : null,
      accrual_type: form.accrual_type,
      reason_for_change: form.reason_for_change || null,
      status: form.status
    };

    try {
      if (editingRule) {
        await api.put(`/api/leave-rules/${editingRule.id}`, payload);
        toast({ title: 'Success', description: 'Leave rule updated' });
      } else {
        await api.post('/api/leave-rules/', payload);
        toast({ title: 'Success', description: 'Leave rule created' });
      }
      setDialogOpen(false);
      setEditingRule(null);
      setForm({ ...DEFAULT_FORM });
      loadData();
    } catch (err) {
      toast({
        title: 'Error',
        description: err.response?.data?.detail || 'Failed to save rule',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (rule) => {
    setEditingRule(rule);
    setForm({
      rule_name: rule.rule_name,
      department: rule.department,
      employee_grade: rule.employee_grade || 'All',
      effective_from: rule.effective_from,
      cycle_start_month: String(rule.cycle_start_month),
      cycle_start_day: String(rule.cycle_start_day),
      cycle_end_month: String(rule.cycle_end_month),
      cycle_end_day: String(rule.cycle_end_day),
      annual_allocation: String(rule.annual_allocation),
      max_carry_forward: String(rule.max_carry_forward),
      max_total_usable: rule.max_total_usable ? String(rule.max_total_usable) : '',
      accrual_type: rule.accrual_type || 'upfront',
      reason_for_change: '',
      status: rule.status
    });
    setDialogOpen(true);
  };

  const handleDeactivate = async (rule) => {
    if (!window.confirm(`Deactivate "${rule.rule_name}"?`)) return;
    try {
      await api.delete(`/api/leave-rules/${rule.id}`);
      toast({ title: 'Success', description: 'Rule deactivated' });
      loadData();
    } catch {
      toast({ title: 'Error', description: 'Failed to deactivate rule', variant: 'destructive' });
    }
  };

  // Auto-calculate max_total_usable
  const autoTotal = (parseFloat(form.annual_allocation) || 0) + (parseFloat(form.max_carry_forward) || 0);

  const getCycleLabel = (rule) => {
    const sm = MONTHS.find(m => m.value === rule.cycle_start_month)?.label || '';
    const em = MONTHS.find(m => m.value === rule.cycle_end_month)?.label || '';
    if (rule.cycle_start_month === 1 && rule.cycle_end_month === 12) return 'Jan - Dec (Calendar Year)';
    return `${sm} ${rule.cycle_start_day} - ${em} ${rule.cycle_end_day}`;
  };

  const today = new Date().toISOString().split('T')[0];
  const activeRules = rules.filter(r => r.status === 'active' && r.effective_from <= today);
  const inactiveRules = rules.filter(r => r.status === 'inactive');

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="leave-rules-tab">
      <MultiDateSettingsCard />
      {/* Header & Actions */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center gap-2">
            <CalendarDays className="h-5 w-5 text-purple-600" />
            Leave Rules Configuration
          </h3>
          <p className="text-sm text-gray-500 mt-0.5">
            Define leave policies, cycles, and carry-forward limits
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => loadAudit(null)} data-testid="view-audit-btn">
            <History className="h-4 w-4 mr-1" /> Audit Log
          </Button>
          {!isViewer && (
            <Button size="sm" onClick={() => { setEditingRule(null); setForm({ ...DEFAULT_FORM }); setDialogOpen(true); }} data-testid="create-rule-btn">
              <Plus className="h-4 w-4 mr-1" /> Create Rule
            </Button>
          )}
        </div>
      </div>

      {/* Carry-Forward Policy (global) */}
      <Card data-testid="carry-forward-policy-card">
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <TrendingUp className="h-4 w-4 text-amber-600" />
            Carry-Forward Policy
          </CardTitle>
          <CardDescription>
            Controls how much unused annual leave rolls into the next year, and from which
            year the cap starts. Cycles ending <strong>before</strong> the cap year carry
            forward in full — so long-serving staff keep leave accrued under the old policy
            (fixes old records being wrongly marked LOP).
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 items-end">
            <div>
              <Label>Max carry-forward (days)</Label>
              <Input
                type="number" min="0" step="0.5"
                value={cfSettings.max_carry_forward}
                onChange={(e) => setCfSettings(s => ({ ...s, max_carry_forward: e.target.value }))}
                disabled={isViewer}
                data-testid="cf-max-input"
              />
            </div>
            <div>
              <Label>Cap starts from year</Label>
              <Input
                type="number" min="2000" max="2100" step="1"
                value={cfSettings.cap_start_year}
                onChange={(e) => setCfSettings(s => ({ ...s, cap_start_year: e.target.value }))}
                disabled={isViewer}
                data-testid="cf-year-input"
              />
              <p className="text-[11px] text-gray-400 mt-1">
                e.g. 2023 = every year up to 2022 is uncapped; the cap first applies at the end of {cfSettings.cap_start_year || 2023}.
              </p>
            </div>
            {!isViewer && (
              <Button onClick={saveCfSettings} disabled={savingCf} data-testid="cf-save-btn">
                {savingCf ? 'Saving…' : 'Save Policy'}
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-green-600" />
            Active Rules ({activeRules.length})
          </CardTitle>
          <CardDescription>Currently enforced leave policies</CardDescription>
        </CardHeader>
        <CardContent>
          {activeRules.length === 0 ? (
            <p className="text-sm text-gray-400 text-center py-4">No active rules. Default policy (30 days, Jan-Dec) is in effect.</p>
          ) : (
            <div className="space-y-3">
              {activeRules.map(rule => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  getCycleLabel={getCycleLabel}
                  onEdit={handleEdit}
                  onDeactivate={handleDeactivate}
                  onViewAudit={(r) => loadAudit(r.id)}
                  isViewer={isViewer}
                  variant="active"
                />
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Upcoming Rules */}
      {upcomingRules.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2">
              <Clock className="h-4 w-4 text-blue-600" />
              Upcoming Rules ({upcomingRules.length})
            </CardTitle>
            <CardDescription>Rules that will take effect in the future</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {upcomingRules.map(rule => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  getCycleLabel={getCycleLabel}
                  onEdit={handleEdit}
                  onDeactivate={handleDeactivate}
                  onViewAudit={(r) => loadAudit(r.id)}
                  isViewer={isViewer}
                  variant="upcoming"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Inactive Rules */}
      {inactiveRules.length > 0 && (
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2 text-gray-500">
              <FileText className="h-4 w-4" />
              Inactive Rules ({inactiveRules.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 opacity-60">
              {inactiveRules.map(rule => (
                <RuleCard
                  key={rule.id}
                  rule={rule}
                  getCycleLabel={getCycleLabel}
                  onEdit={handleEdit}
                  onDeactivate={handleDeactivate}
                  onViewAudit={(r) => loadAudit(r.id)}
                  isViewer={isViewer}
                  variant="inactive"
                />
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={(o) => { if (!o) { setEditingRule(null); setForm({ ...DEFAULT_FORM }); } setDialogOpen(o); }}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="rule-dialog">
          <DialogHeader>
            <DialogTitle>{editingRule ? 'Edit Leave Rule' : 'Create Leave Rule'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            {/* Row 1: Rule Name + Department */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Rule Name *</Label>
                <Input
                  value={form.rule_name}
                  onChange={(e) => setForm({ ...form, rule_name: e.target.value })}
                  placeholder="e.g., Standard Policy"
                  data-testid="rule-name-input"
                />
              </div>
              <div>
                <Label>Department</Label>
                <Select value={form.department} onValueChange={(v) => setForm({ ...form, department: v })}>
                  <SelectTrigger data-testid="rule-dept-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Departments (Global)</SelectItem>
                    {departments.map(d => <SelectItem key={d} value={d}>{d}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 1b: Employee Grade + Accrual Type */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Employee Grade</Label>
                <Select value={form.employee_grade} onValueChange={(v) => setForm({ ...form, employee_grade: v })}>
                  <SelectTrigger data-testid="rule-grade-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {GRADES.map(g => <SelectItem key={g} value={g}>{g === 'All' ? 'All Grades' : g}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Accrual Type</Label>
                <Select value={form.accrual_type} onValueChange={(v) => setForm({ ...form, accrual_type: v })}>
                  <SelectTrigger data-testid="rule-accrual-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {ACCRUAL_TYPES.map(a => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Row 2: Effective From + Status */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Effective From *</Label>
                <Input
                  type="date"
                  value={form.effective_from}
                  onChange={(e) => setForm({ ...form, effective_from: e.target.value })}
                  data-testid="rule-effective-from"
                />
              </div>
              <div className="flex items-end gap-3 pb-1">
                <Label className="text-sm">Status</Label>
                <div className="flex items-center gap-2">
                  <Switch
                    checked={form.status === 'active'}
                    onCheckedChange={(c) => setForm({ ...form, status: c ? 'active' : 'inactive' })}
                    data-testid="rule-status-switch"
                  />
                  <span className="text-sm">{form.status === 'active' ? 'Active' : 'Inactive'}</span>
                </div>
              </div>
            </div>

            {/* Leave Cycle */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <Label className="text-sm font-semibold text-gray-700 mb-3 block">Leave Cycle Period</Label>
              <div className="grid grid-cols-4 gap-3">
                <div>
                  <Label className="text-xs">Start Month</Label>
                  <Select value={form.cycle_start_month} onValueChange={(v) => setForm({ ...form, cycle_start_month: v })}>
                    <SelectTrigger data-testid="rule-cycle-start-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">Start Day</Label>
                  <Select value={form.cycle_start_day} onValueChange={(v) => setForm({ ...form, cycle_start_day: v })}>
                    <SelectTrigger data-testid="rule-cycle-start-day"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: DAYS_IN_MONTH[parseInt(form.cycle_start_month)] || 31 }, (_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">End Month</Label>
                  <Select value={form.cycle_end_month} onValueChange={(v) => setForm({ ...form, cycle_end_month: v })}>
                    <SelectTrigger data-testid="rule-cycle-end-month"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {MONTHS.map(m => <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-xs">End Day</Label>
                  <Select value={form.cycle_end_day} onValueChange={(v) => setForm({ ...form, cycle_end_day: v })}>
                    <SelectTrigger data-testid="rule-cycle-end-day"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: DAYS_IN_MONTH[parseInt(form.cycle_end_month)] || 31 }, (_, i) => (
                        <SelectItem key={i + 1} value={String(i + 1)}>{i + 1}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Cycle: {MONTHS.find(m => m.value === parseInt(form.cycle_start_month))?.label} {form.cycle_start_day} &rarr; {MONTHS.find(m => m.value === parseInt(form.cycle_end_month))?.label} {form.cycle_end_day}
              </p>
            </div>

            {/* Leave Allocation */}
            <div className="border rounded-lg p-4 bg-gray-50">
              <Label className="text-sm font-semibold text-gray-700 mb-3 block">Leave Allocation</Label>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label className="text-xs">Annual Allocation (days)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.annual_allocation}
                    onChange={(e) => setForm({ ...form, annual_allocation: e.target.value })}
                    data-testid="rule-annual-allocation"
                  />
                </div>
                <div>
                  <Label className="text-xs">Max Carry Forward (days)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.max_carry_forward}
                    onChange={(e) => setForm({ ...form, max_carry_forward: e.target.value })}
                    data-testid="rule-max-carry-forward"
                  />
                </div>
                <div>
                  <Label className="text-xs">Max Total Usable (days)</Label>
                  <Input
                    type="number"
                    min="0"
                    value={form.max_total_usable || ''}
                    onChange={(e) => setForm({ ...form, max_total_usable: e.target.value })}
                    placeholder={`Auto: ${autoTotal}`}
                    data-testid="rule-max-total"
                  />
                  <p className="text-[10px] text-gray-400 mt-0.5">Leave blank for auto ({autoTotal})</p>
                </div>
              </div>
            </div>

            {/* Reason */}
            <div>
              <Label>Reason for Change (optional)</Label>
              <Textarea
                value={form.reason_for_change}
                onChange={(e) => setForm({ ...form, reason_for_change: e.target.value })}
                placeholder="Describe why this rule is being created/modified..."
                rows={2}
                data-testid="rule-reason-input"
              />
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => { setDialogOpen(false); setEditingRule(null); }}>Cancel</Button>
              <Button onClick={handleSave} data-testid="save-rule-btn">
                {editingRule ? 'Update Rule' : 'Create Rule'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Audit Log Dialog */}
      <Dialog open={auditDialogOpen} onOpenChange={setAuditDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="audit-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-5 w-5 text-purple-600" />
              Rule Change Audit Log
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-3 pt-2">
            {auditLog.length === 0 ? (
              <p className="text-sm text-gray-400 text-center py-6">No audit entries found</p>
            ) : (
              auditLog.map((entry, i) => (
                <div key={entry.id || i} className="border rounded-lg p-3 text-sm" data-testid={`audit-entry-${i}`}>
                  <div className="flex items-center justify-between mb-1">
                    <div className="flex items-center gap-2">
                      <Badge variant={entry.action === 'created' ? 'default' : entry.action === 'deactivated' ? 'destructive' : 'secondary'}>
                        {entry.action}
                      </Badge>
                      <span className="font-medium">{entry.rule_name}</span>
                      {entry.department && entry.department !== 'All' && (
                        <Badge variant="outline" className="text-xs">{entry.department}</Badge>
                      )}
                    </div>
                    <span className="text-xs text-gray-400">
                      {entry.changed_at ? new Date(entry.changed_at).toLocaleString() : ''}
                    </span>
                  </div>
                  {entry.reason && (
                    <p className="text-xs text-gray-500 mt-1">Reason: {entry.reason}</p>
                  )}
                  <p className="text-xs text-gray-400 mt-1">By: {entry.changed_by || 'System'}</p>
                </div>
              ))
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// ── Rule Card Component ──────────────────────────────────────────

const RuleCard = ({ rule, getCycleLabel, onEdit, onDeactivate, onViewAudit, isViewer, variant }) => {
  const borderColor = variant === 'active' ? 'border-green-200' : variant === 'upcoming' ? 'border-blue-200' : 'border-gray-200';
  const badgeVariant = variant === 'active' ? 'default' : variant === 'upcoming' ? 'secondary' : 'outline';
  const accrualLabel = rule.accrual_type === 'monthly' ? 'Monthly' : rule.accrual_type === 'quarterly' ? 'Quarterly' : 'Upfront';

  return (
    <div className={`flex items-start justify-between p-4 rounded-lg border ${borderColor} bg-white`} data-testid={`rule-card-${rule.id}`}>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 mb-1 flex-wrap">
          <h4 className="font-semibold text-sm">{rule.rule_name}</h4>
          <Badge variant={badgeVariant} className="text-[10px]">{variant}</Badge>
          <Badge variant="outline" className="text-[10px]">
            <Building2 className="h-3 w-3 mr-1" />
            {rule.department === 'All' ? 'All Depts' : rule.department}
          </Badge>
          {rule.employee_grade && rule.employee_grade !== 'All' && (
            <Badge variant="outline" className="text-[10px]">
              <Users className="h-3 w-3 mr-1" />
              {rule.employee_grade}
            </Badge>
          )}
          {rule.accrual_type && rule.accrual_type !== 'upfront' && (
            <Badge variant="outline" className="text-[10px] border-purple-200 text-purple-700">
              <TrendingUp className="h-3 w-3 mr-1" />
              {accrualLabel}
            </Badge>
          )}
        </div>
        <div className="grid grid-cols-5 gap-3 mt-2 text-xs text-gray-600">
          <div>
            <span className="text-gray-400 block">Cycle</span>
            <span className="font-medium">{getCycleLabel(rule)}</span>
          </div>
          <div>
            <span className="text-gray-400 block">Allocation</span>
            <span className="font-medium">{rule.annual_allocation} days</span>
          </div>
          <div>
            <span className="text-gray-400 block">Carry Forward</span>
            <span className="font-medium">Max {rule.max_carry_forward} days</span>
          </div>
          <div>
            <span className="text-gray-400 block">Accrual</span>
            <span className="font-medium">{accrualLabel}{rule.accrual_rate ? ` (${rule.accrual_rate}d)` : ''}</span>
          </div>
          <div>
            <span className="text-gray-400 block">Effective From</span>
            <span className="font-medium">{new Date(rule.effective_from + 'T00:00:00').toLocaleDateString('en-GB', { day: '2-digit', month: '2-digit', year: 'numeric' })}</span>
          </div>
        </div>
      </div>
      {!isViewer && (
        <div className="flex items-center gap-1 ml-3 flex-shrink-0">
          <Button size="sm" variant="ghost" onClick={() => onViewAudit(rule)} title="View History">
            <History className="h-3.5 w-3.5 text-gray-400" />
          </Button>
          <Button size="sm" variant="ghost" onClick={() => onEdit(rule)} title="Edit">
            <Edit className="h-3.5 w-3.5" />
          </Button>
          {rule.status === 'active' && (
            <Button size="sm" variant="ghost" onClick={() => onDeactivate(rule)} title="Deactivate" className="text-red-500">
              <Trash2 className="h-3.5 w-3.5" />
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default LeaveRulesTab;
